import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DateFormatConstants, isNullOrUndefined, TimeZoneUtil } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CancellationReason,
  InterestedDriver,
  JourneyInterestedDriver,
  Provider,
  ProviderSelectionDetail,
  ReservationDetail,
  RideDetail,
  RideRequest,
  RideRequestDetail,
  RideRequestFeedItem,
  RideRequestType,
  Trip
} from '../../models';

@Injectable({
  providedIn: 'root'
})
export class RideRequestService extends UzurvRestService {
  readonly resourceUrl = 'rideRequests/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Converts a RideDetail into a RideRequestFeedItem.
   *
   * @param rideDetail
   */
  static mapRideDetailToRideRequestFeedItem(rideDetail: RideDetail): RideRequestFeedItem {
    const rideRequestFeedItem: RideRequestFeedItem = new RideRequestFeedItem();

    rideRequestFeedItem.id = rideDetail.rideRequestId;
    rideRequestFeedItem.activated = rideDetail.activated;
    rideRequestFeedItem.dropoffLocation = rideDetail.dropoffLocation;
    rideRequestFeedItem.incentive = rideDetail.incentive;
    rideRequestFeedItem.pickupDate = rideDetail.pickupDate;
    rideRequestFeedItem.pickupLocation = rideDetail.pickupLocation;
    rideRequestFeedItem.rideRequestType = rideDetail.rideRequestType;
    rideRequestFeedItem.distanceEstimate = rideDetail.distanceEstimate;
    rideRequestFeedItem.timeZone = rideDetail.timeZone;
    rideRequestFeedItem.driverFareEstimate = rideDetail.driverFareEstimate;
    rideRequestFeedItem.transitProgramInfo = rideDetail.transitProgramInfo;
    return rideRequestFeedItem;
  }

  /**
   * Returns a list of ride requests
   *
   * @param pageNumber
   * @param pageSize
   * @param transitProgramIds
   * @param vehicleTypeIds
   * @permission ride_monitor
   */
  listRideRequests(
    pageNumber: number,
    pageSize: number,
    transitProgramIds?: number | Array<number>,
    vehicleTypeIds?: number | Array<number>
  ): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(this.resourceUrl, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      transitProgramIds: transitProgramIds,
      vehicleTypeIds: vehicleTypeIds
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @permission nonaccepted_ride_read
   */
  listNonacceptedRideRequests(pageNumber: number, pageSize: number): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(this.resourceUrl, {
      nonaccepted: true,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * Returns a list of ride request types
   *
   * @permission ride_request_read
   */
  listRideRequestTypes(): Observable<Array<RideRequestType>> {
    return this.get<Array<RideRequestType>>(`${this.resourceUrl}rideRequestTypes/`).pipe(
      map(response => Deserialize(response, RideRequestType))
    );
  }

  /**
   * Returns a list of cancellation reasons, optionally overriding the cached values
   *
   * @param overrideCache
   */
  listRideRequestCancellationReasons(): Observable<Array<CancellationReason>> {
    return this.get<Array<CancellationReason>>(`${this.resourceUrl}cancellationReasons/`).pipe(
      map(response => Deserialize(response, CancellationReason))
    );
  }

  /**
   * @param id
   * @permission ride_request_read
   */
  findById(id: number): Observable<RideRequestDetail> {
    return this.get(`${this.resourceUrl}${id}`).pipe(map(response => Deserialize(response, RideRequestDetail)));
  }

  /**
   * Finds the active reservation for a given ride request
   *
   * @param rideRequestId
   * @permission reservation_read
   */
  findActiveReservationByRideRequestId(rideRequestId: number): Observable<ReservationDetail> {
    return this.get<ReservationDetail>(`${this.resourceUrl}${rideRequestId}/currentReservation`).pipe(
      map(response => Deserialize(response, ReservationDetail))
    );
  }

  /**
   * Returns a list of interested drivers for a ride request
   *
   * @param rideRequestId
   * @permission ride_monitoring
   */
  getInterestedDriversByRideRequestId(rideRequestId: number): Observable<Array<InterestedDriver>> {
    return this.get<Array<InterestedDriver>>(`${this.resourceUrl}${rideRequestId}/interestedDrivers/`).pipe(
      map(response => Deserialize(response, InterestedDriver))
    );
  }

  /**
   * Returns a list of available providers for a ride
   *
   * @param rideRequestId
   * @permission ride_monitoring
   */
  listPairableProvidersByRideRequestId(rideRequestId: number): Observable<Array<ProviderSelectionDetail>> {
    return this.get<Array<ProviderSelectionDetail>>(`${this.resourceUrl}${rideRequestId}/pairableProviders/`).pipe(
      map(response => Deserialize(response, ProviderSelectionDetail))
    );
  }

  /**
   * Returns a list of journey interested drivers for a ride request
   *
   * @param rideRequestId
   * @permission ride_monitoring
   */
  listJourneyInterestedDriversByRideRequestId(rideRequestId: number): Observable<Array<JourneyInterestedDriver>> {
    return this.get<Array<JourneyInterestedDriver>>(`${this.resourceUrl}${rideRequestId}/journeyInterestedDrivers/`, {
      manuallyPairable: true
    }).pipe(map(response => Deserialize(response, JourneyInterestedDriver)));
  }

  /**
   *
   * @param rideRequestId
   * @permission ride_monitoring
   */
  listProvidersIgnoringRideRequestById(rideRequestId: number): Observable<Array<Provider>> {
    return this.get<Array<Provider>>(`${this.resourceUrl}${rideRequestId}/providersIgnoringRideRequest/`).pipe(
      map(response => Deserialize(response, Provider))
    );
  }

  /**
   * Patch a ride request in an itinerary. Use this to update an incentive
   *
   * @param rideRequestId
   * @param patchDict
   * @permission ride_request_update
   */
  patchRideRequest(rideRequestId: number, patchDict: { incentive?: number }): Observable<RideRequest> {
    patchDict['id'] = rideRequestId;
    return this.patch(`${this.resourceUrl}${rideRequestId}`, patchDict).pipe(map(response => Deserialize(response, RideRequest)));
  }

  // Utils

  // TODO: Refactor this to use a 'hasPickupDate' (?) interface so we can have a single method here
  sortRideRequestsByPickupDate(rideRequests: Array<RideRequest>, ascending = true): Array<RideRequest> {
    if (isNullOrUndefined(rideRequests)) {
      return [];
    }

    return rideRequests.sort((a, b) => {
      let aPickupDate = a.pickupDate;
      let bPickupDate = b.pickupDate;

      if (isNullOrUndefined(aPickupDate)) {
        aPickupDate = new Date();
      }
      if (isNullOrUndefined(bPickupDate)) {
        bPickupDate = new Date();
      }

      if (ascending) {
        return aPickupDate.getTime() - bPickupDate.getTime();
      } else {
        return bPickupDate.getTime() - aPickupDate.getTime();
      }
    });
  }

  sortRideRequestFeedItemsByPickupDate(rideRequests: Array<RideRequestFeedItem>, ascending = true): Array<RideRequestFeedItem> {
    if (isNullOrUndefined(rideRequests)) {
      return [];
    }

    return rideRequests.sort((a, b) => {
      let aPickupDate = a.pickupDate;
      let bPickupDate = b.pickupDate;

      if (isNullOrUndefined(aPickupDate)) {
        aPickupDate = new Date();
      }
      if (isNullOrUndefined(bPickupDate)) {
        bPickupDate = new Date();
      }

      if (ascending) {
        return aPickupDate.getTime() - bPickupDate.getTime();
      } else {
        return bPickupDate.getTime() - aPickupDate.getTime();
      }
    });
  }

  // Pickup Date

  /**
   *
   * @param pickupDate
   * @param timeZone
   * @param dateFormat
   */
  getFormattedPickupDate(pickupDate: Date, timeZone: string, dateFormat = DateFormatConstants.DATE_FORMAT_FULL): string {
    return TimeZoneUtil.formatTimeZoneDate(pickupDate, timeZone, dateFormat);
  }
}
